import { API } from './interceptor'
import { mapGrowthEngineRecommendationPayload } from './growthEngineRecommendation'
import { mapGrowthEngineSuccessHubPayload } from './growthEngineSuccessHub'

/** Platforms Growth Engine nudge clients should send. */
export const GROWTH_ENGINE_PLATFORMS = Object.freeze(['dashboard', 'app'])

/** Interaction values for POST .../recommendations/interactions. */
export const GROWTH_ENGINE_INTERACTIONS = Object.freeze([
  'shown',
  'clicked',
  'snoozed',
  'dismissed',
  'ignored',
  'accepted',
  'completed',
  'unsnoozed',
])

/** Feature-page Guided Success actions — intentId defaults on Express. */
export const GROWTH_ENGINE_GUIDED_SUCCESS_INTERACTIONS = Object.freeze([
  'empty_state_viewed',
  'empty_state_cta_clicked',
  'suggested_data_shown',
  'suggested_data_accepted',
  'suggested_data_rejected',
  'walkthrough_started',
  'walkthrough_step_completed',
  'walkthrough_resumed',
  'walkthrough_abandoned',
  'walkthrough_skipped',
  'walkthrough_completed',
  'first_success_achieved',
])

export function isGrowthEngineGuidedSuccessInteraction(interaction) {
  return GROWTH_ENGINE_GUIDED_SUCCESS_INTERACTIONS.includes(interaction)
}

/**
 * Next Growth Engine recommendation for a venue (may be null).
 *
 * @param {string} venueId
 * @param {{
 *   platform?: string,
 *   product?: string,
 *   days?: string|number,
 *   orderCount?: string|number,
 *   n?: string|number,
 *   surface?: string,
 *   featureId?: string,
 * }} [queryParams]
 */
export async function fetchGrowthEngineRecommendations(
  venueId,
  queryParams = {},
) {
  if (!venueId) {
    throw new Error('venueId is required')
  }

  const response = await API.get(
    'api2',
    `growth-engine/venues/${venueId}/recommendations`,
    {
      queryStringParameters: {
        platform: queryParams.platform || 'dashboard',
        ...pickPersonalisation(queryParams),
      },
    },
  )

  if (!response?.success) {
    throw new Error(
      response?.message || 'Failed to load Growth Engine recommendations',
    )
  }

  return mapGrowthEngineRecommendationPayload({
    ...response,
    platform: response.platform || queryParams.platform || 'dashboard',
  })
}

/**
 * Success Hub Progress payload (today, missions, tiles, score, health).
 * Express stamps `surface=success_hub` — do not send surface / featureId.
 *
 * @param {string} venueId
 * @param {{
 *   platform?: string,
 *   product?: string,
 *   days?: string|number,
 *   orderCount?: string|number,
 *   n?: string|number,
 * }} [queryParams]
 */
export async function fetchGrowthEngineSuccessHub(venueId, queryParams = {}) {
  if (!venueId) {
    throw new Error('venueId is required')
  }

  const response = await API.get(
    'api2',
    `growth-engine/venues/${venueId}/success-hub`,
    {
      queryStringParameters: {
        platform: queryParams.platform || 'dashboard',
        ...pickCopyPersonalisation(queryParams),
      },
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load Progress')
  }

  return mapGrowthEngineSuccessHubPayload({
    ...response,
    platform: response.platform || queryParams.platform || 'dashboard',
  })
}

/**
 * Record a recommendation interaction (shown / clicked / snoozed / etc.).
 *
 * @param {string} venueId
 * @param {{
 *   intentId: string,
 *   interaction: string,
 *   featureId?: string,
 *   platform?: string,
 *   metadata?: Record<string, unknown>,
 * }} body
 */
export async function postGrowthEngineRecommendationInteraction(
  venueId,
  body = {},
) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (!body.interaction) {
    throw new Error('interaction is required')
  }
  const guided = isGrowthEngineGuidedSuccessInteraction(body.interaction)
  if (!guided && !body.intentId) {
    throw new Error('intentId is required')
  }
  if (guided && !body.featureId && !body.intentId) {
    throw new Error('featureId is required')
  }

  const response = await API.post(
    'api2',
    `growth-engine/venues/${venueId}/recommendations/interactions`,
    {
      body: {
        ...(body.intentId ? { intentId: body.intentId } : {}),
        interaction: body.interaction,
        featureId: body.featureId,
        platform: body.platform || 'dashboard',
        metadata: body.metadata || {},
      },
    },
  )

  if (!response?.success) {
    throw new Error(
      response?.message ||
        'Failed to record Growth Engine recommendation interaction',
    )
  }

  return {
    interaction: response.interaction,
    platform: response.platform || body.platform || 'dashboard',
    plan: response.plan ?? null,
    intentState: response.intentState ?? null,
  }
}

/**
 * Generic Growth Engine analytics (feature opened / first used / etc.).
 * Prefer {@link postGrowthEngineRecommendationInteraction} for nudge UI actions.
 *
 * @param {string} venueId
 * @param {{
 *   eventName: string,
 *   featureId?: string,
 *   intentId?: string,
 *   variantIndex?: number,
 *   platform?: string,
 *   plan?: string,
 *   occurredAt?: string,
 *   metadata?: Record<string, unknown>,
 * }} body
 */
export async function postGrowthEngineAnalytics(venueId, body = {}) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (!body.eventName) {
    throw new Error('eventName is required')
  }

  const response = await API.post(
    'api2',
    `growth-engine/venues/${venueId}/analytics`,
    {
      body: {
        eventName: body.eventName,
        featureId: body.featureId,
        intentId: body.intentId,
        variantIndex: body.variantIndex,
        platform: body.platform || 'dashboard',
        plan: body.plan,
        occurredAt: body.occurredAt,
        metadata: body.metadata || {},
      },
    },
  )

  if (!response?.success) {
    throw new Error(
      response?.message || 'Failed to record Growth Engine analytics event',
    )
  }

  return {
    canonical: Boolean(response.canonical),
    event: response.event ?? null,
  }
}

/**
 * Prefs + kitchen days for Settings. Does not count as a Progress visit.
 *
 * @param {string} venueId
 */
export async function fetchGrowthEngineSuccessHubSettings(venueId) {
  if (!venueId) {
    throw new Error('venueId is required')
  }

  const response = await API.get(
    'api2',
    `growth-engine/venues/${venueId}/success-hub/settings`,
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load Progress settings')
  }

  return {
    prefs: response.prefs || { celebrations: { enabled: true } },
    businessDays: response.businessDays || null,
  }
}

/**
 * Save account-scoped celebrations on/off.
 *
 * @param {string} venueId
 * @param {{ celebrations: { enabled: boolean } }} prefs
 */
const OLLIE_PUSH_SETTINGS_FALLBACK = Object.freeze({
  enabled: true,
  canManage: false,
  updatedAt: null,
})

/**
 * Ollie phone alerts for the venue (step 53): on/off and whether this person
 * can change it (venue admins only).
 *
 * @param {string} venueId
 */
export async function fetchOlliePushSettings(venueId) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  const response = await API.get(
    'api2',
    `growth-engine/venues/${venueId}/ollie/push-settings`,
  )
  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load Ollie alerts')
  }
  return { ...OLLIE_PUSH_SETTINGS_FALLBACK, ...(response.olliePush || {}) }
}

/**
 * @param {string} venueId
 * @param {boolean} enabled
 */
export async function postOlliePushSettings(venueId, enabled) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (typeof enabled !== 'boolean') {
    throw new Error('enabled must be true or false')
  }
  const response = await API.post(
    'api2',
    `growth-engine/venues/${venueId}/ollie/push-settings`,
    { body: { enabled, platform: 'dashboard' } },
  )
  if (!response?.success) {
    throw new Error(response?.message || 'Failed to save Ollie alerts')
  }
  return { ...OLLIE_PUSH_SETTINGS_FALLBACK, ...(response.olliePush || {}) }
}

export async function postGrowthEngineSuccessHubPrefs(venueId, prefs = {}) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (!prefs.celebrations || typeof prefs.celebrations !== 'object') {
    throw new Error('celebrations is required')
  }

  const response = await API.post(
    'api2',
    `growth-engine/venues/${venueId}/success-hub/prefs`,
    {
      body: { celebrations: prefs.celebrations },
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to save Progress prefs')
  }

  return { prefs: response.prefs || prefs }
}

/**
 * Confirm (or clear) venue ordering days. Empty array turns streaks off.
 *
 * @param {string} venueId
 * @param {number[]} weekdays
 */
export async function postGrowthEngineSuccessHubBusinessDays(
  venueId,
  weekdays,
) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (!Array.isArray(weekdays)) {
    throw new Error('weekdays is required')
  }

  const response = await API.post(
    'api2',
    `growth-engine/venues/${venueId}/success-hub/business-days`,
    {
      body: { weekdays },
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to save kitchen days')
  }

  return { businessDays: response.businessDays || null }
}

function pickCopyPersonalisation(queryParams = {}) {
  const personalisation = {}
  if (queryParams.product != null && queryParams.product !== '') {
    personalisation.product = String(queryParams.product)
  }
  if (queryParams.days != null && queryParams.days !== '') {
    personalisation.days = queryParams.days
  }
  if (queryParams.orderCount != null && queryParams.orderCount !== '') {
    personalisation.orderCount = queryParams.orderCount
  }
  if (queryParams.n != null && queryParams.n !== '') {
    personalisation.n = queryParams.n
  }
  return personalisation
}

function pickPersonalisation(queryParams = {}) {
  const personalisation = pickCopyPersonalisation(queryParams)
  if (queryParams.surface != null && queryParams.surface !== '') {
    personalisation.surface = String(queryParams.surface)
  }
  if (queryParams.featureId != null && queryParams.featureId !== '') {
    personalisation.featureId = String(queryParams.featureId)
  }
  return personalisation
}
